import { useRef, useEffect, useCallback, useState } from 'react';

// ============================================================================
// usePullToRefresh — Touch-based pull-to-refresh for mobile
// Attaches to a scrollable container and triggers a callback on pull gesture.
// Only active on touch devices when the container is scrolled to the top.
// ============================================================================

interface PullToRefreshOptions {
  /** Callback to invoke on refresh (should return a Promise) */
  onRefresh: () => Promise<void>;
  /** Minimum pull distance in pixels to trigger refresh (default: 80) */
  threshold?: number;
  /** Whether the hook is enabled (default: true) */
  enabled?: boolean;
}

interface PullToRefreshState {
  /** Whether a refresh is currently in progress */
  isRefreshing: boolean;
  /** Current pull distance in pixels (0 when not pulling) */
  pullDistance: number;
}

/**
 * Lightweight pull-to-refresh hook for mobile.
 * Returns a ref to attach to the scrollable container.
 *
 * Usage:
 * ```tsx
 * const { ref, isRefreshing, pullDistance } = usePullToRefresh({
 *   onRefresh: async () => { await queryClient.invalidateQueries(); },
 * });
 * return <div ref={ref}>...</div>;
 * ```
 */
export function usePullToRefresh<T extends HTMLElement = HTMLElement>({
  onRefresh,
  threshold = 80,
  enabled = true,
}: PullToRefreshOptions) {
  const ref = useRef<T>(null);
  const [state, setState] = useState<PullToRefreshState>({
    isRefreshing: false,
    pullDistance: 0,
  });

  const startYRef = useRef(0);
  const pullingRef = useRef(false);

  const handleTouchStart = useCallback(
    (e: TouchEvent) => {
      if (!enabled || state.isRefreshing) return;
      const container = ref.current;
      if (!container) return;

      // Only start tracking if scrolled to top
      if (container.scrollTop <= 0) {
        startYRef.current = e.touches[0].clientY;
        pullingRef.current = true;
      }
    },
    [enabled, state.isRefreshing],
  );

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!pullingRef.current || state.isRefreshing) return;

      const currentY = e.touches[0].clientY;
      const distance = Math.max(0, currentY - startYRef.current);

      // Apply resistance — diminishing returns as you pull farther
      const dampened = Math.min(distance * 0.4, threshold * 1.5);

      if (dampened > 0) {
        setState((s) => ({ ...s, pullDistance: dampened }));
        // Prevent native scroll when pulling down at top
        if (ref.current && ref.current.scrollTop <= 0 && distance > 10) {
          e.preventDefault();
        }
      }
    },
    [state.isRefreshing, threshold],
  );

  const handleTouchEnd = useCallback(async () => {
    if (!pullingRef.current) return;
    pullingRef.current = false;

    if (state.pullDistance >= threshold) {
      setState({ isRefreshing: true, pullDistance: 0 });
      try {
        await onRefresh();
      } finally {
        setState({ isRefreshing: false, pullDistance: 0 });
      }
    } else {
      setState((s) => ({ ...s, pullDistance: 0 }));
    }
  }, [state.pullDistance, threshold, onRefresh]);

  useEffect(() => {
    const container = ref.current;
    if (!container || !enabled) return;

    // Use passive: false for touchmove so we can preventDefault
    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd, enabled]);

  return {
    ref,
    isRefreshing: state.isRefreshing,
    pullDistance: state.pullDistance,
  };
}
